import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedHTMLMessage, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import HSFetch from 'hellospa/hs-fetch';
import Table from 'hellospa/foundation/tables/table';
import { Button as DigButton, IconButton } from 'dig-components/buttons';
import { KeyLine } from 'dig-components/dist/icons/src';
import { UIIcon } from 'dig-components/icons';
import TextField from 'hellospa/foundation/control/text-field';
import { FormHelperText } from 'dig-components/text_fields';
import { Snackbar } from 'dig-components/snackbar';
import {
  CheckmarkLine,
  CopyLine,
  CursorLine,
  DeleteLine,
  FailLine,
  InfoSmallLine,
  MoreVerticalLine,
} from 'dig-components/icons/src';
import { Spinner } from 'dig-components/progress_indicators';
import { Tooltip } from 'dig-components/tooltips';
import { Menu } from 'dig-components/menu';
import RelativeDate from 'hellospa/common/utils/relative-date';
import HelloModal from 'common/components/hello-modal';
import Button, { ButtonType } from 'hellospa/foundation/control/button';
import TextTag, { TextTagType } from 'hellospa/foundation/ui/text-tag';
import LegacyKeyWarningBanner from './legay-key-warning-banner';
import styles from './api-setting.scss';
import Body, { BodyType } from 'hellospa/foundation/typography/body';
import { Text } from 'dig-components/typography';
import Spacer, { SpacerSize } from 'hellospa/foundation/spacing/spacer';
import { trackHeapCustomEvent } from 'common/utils/heap';

const messages = defineMessages({
  generateModalTitle: {
    id: '',
    description: 'The title of the modal when the user is generating new API key',
    defaultMessage: 'New key details',
  },
  markPrimaryModalTitle: {
    id: '',
    description: 'The title of the modal when the user is marking API key as primary key',
    defaultMessage: 'Make ‘{apiKeyName}’ the primary API key?',
  },
  renameModalTitle: {
    id: '',
    description: 'The title of the modal when the user is renaming new API key',
    defaultMessage: 'Enter a new name',
  },
  deleteModalTitle: {
    id: '',
    description: 'The title of the modal when the user is deleting new API key',
    defaultMessage: 'Are you sure you want to delete this key?',
  },
  deleteWarningModalTitle: {
    id: '',
    description: 'The title of the warning modal when the user is deleting primary key',
    defaultMessage: 'Can’t delete key',
  },
  copyConfirmation: {
    id: '',
    description: 'Snackbar text shown when the user copies API key successfully',
    defaultMessage: 'Value copied to clipboard',
  },
  copyError: {
    id: '',
    description: 'Snackbar error text shown when the user copies API key fails',
    defaultMessage: 'Copy failed. Please try different browser.',
  },
  renameConfirmation: {
    id: '',
    description: 'Snackbar text shown when the user rename API key successfully',
    defaultMessage: 'API key renamed',
  },
  deleteConfirmation: {
    id: '',
    description: 'Text for a message displayed to the user noting that the item was deleted.',
    defaultMessage: '{itemName} deleted',
  },
  apiKeyNameLabel: {
    id: '',
    description: 'Aria label for an input text which will shown when generating and renaming API key.',
    defaultMessage: 'Name of API key',
  },
  moreOptions: {
    id: '',
    description: 'Aria label for an vertical menu which is used for each API key actions.',
    defaultMessage: 'More options',
  },
  apiKeyNamePlaceholder: {
    id: '',
    description: 'Placeholder text for a API key input text when generating and renaming.',
    defaultMessage: 'Provide a meaningful name, e.g. Test Key',
  },
  loadingSpinnerAriaValue: {
    id: '',
    description: 'Aria value text for loading message while fetching API key.',
    defaultMessage: 'Loading...',
  },
  primaryKeyInfoTooltip: {
    id: '',
    description: 'Text for a hover tooltip on primary key header row in API key list table',
    defaultMessage: 'Your primary key will be used for callback event verification',
  },
  legacyKeyUpdateTooltip: {
    id: '',
    description: 'Text for a hover tooltip on warning icon besides legacy API key',
    defaultMessage: 'Update this key',
  },
  charLimitApiKey: {
    id: '',
    description: 'Error message shown to used when limit exceeds 25 chars',
    defaultMessage: 'Key name must be 25 characters or less',
  },
  copyApiKeyTooltip: {
    id: '',
    description: 'Text for a hover tooltip on copy button icon besides API key value',
    defaultMessage: 'Click to copy full API key',
  },
});

interface ApiKeyAction {
  guid: string,
  name: string,
  isPrimaryKey: boolean,
  isLegacyKey: boolean,
  action: string,
}

// not camelcase because that's what coming from backend api
/* eslint-disable camelcase */
interface ApiKeyData {
  api_key: string,
  created_at?: string,
  guid: string,
  is_legacy_key: boolean,
  is_primary_key: boolean,
  last_used_at?: string,
  name?: string,
  updated_at?: string,
}
/* eslint-enable camelcase */
const activeActionDataInitialState = {
  guid: '',
  name: '',
  isPrimaryKey: false,
  isLegacyKey: false,
  action: '',
};

function APIKeySection() {

  const intl = useIntl();
  const [apiKeys, setApiKeys] = useState<ApiKeyData[]>([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isGenerateNewAPIKeyModalOpen, setIsGenerateNewAPIKeyModalOpen] = useState(false);
  const [disableGenerateKeyButton, setDisableGenerateKeyButton] = useState(false);
  const [disableRenameKeyButton, setDisableRenameKeyButton] = useState(false);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [displayLegacyKeyWarning, setDisplayLegacyKeyWarning] = useState(false);

  const displayActionError = actionError ? (
    <FormHelperText isInvalid>
      <UIIcon src={FailLine} size="small" />
      {actionError}
    </FormHelperText>
  ) : null;
  const [activeActionData, setActiveActionData] = useState(activeActionDataInitialState);
  const closeSnackbar = React.useCallback((evt:
     React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    evt.preventDefault();
    setOpenSnackBar(false);
  }, []);
  const showSnackbar = React.useCallback(() => setOpenSnackBar(true), []);
  const openGenerateNewApiKeyModal = React.useCallback((evt:
    React.MouseEvent<HTMLButtonElement>) => {
  // Note: to prevent unnecessary submit call which are happening due to old page
    evt.preventDefault();
    setDisableGenerateKeyButton(true);
    setIsGenerateNewAPIKeyModalOpen(true);
  }, []);
  const abortGenerateNewApiKeyModal = () => {
    setActionError('');
    setIsGenerateNewAPIKeyModalOpen(false);
  };
  const abortConfirmationModal = React.useCallback(() => {
    setActionError('');
    setActiveActionData(activeActionDataInitialState);
  }, []);
  const validate = (
    name: string,
    elementId: string,
    options: { checkLength?: boolean } = {},
  ) => {
    if (options.checkLength && name.length > 25) {
      setActionError(intl.formatMessage(messages.charLimitApiKey));
      setDisableGenerateKeyButton(true);
      setDisableRenameKeyButton(true);
    }
  };

  const changeName: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
    setActionError('');
    setDisableGenerateKeyButton(false);
    setDisableRenameKeyButton(false);
    validate(evt.currentTarget.value, evt.target.id, {
      checkLength: true,
    });
  };
  const copyToClipboard = async (value: string) => {
    let snackBarCopyMessage: MessageDescriptor;
    try {
      await navigator.clipboard.writeText(value);
      snackBarCopyMessage = messages.copyConfirmation;
      trackHeapCustomEvent('api_key_copy_success');
    } catch (error) {
      trackHeapCustomEvent('api_key_copy_failure');
      snackBarCopyMessage = messages.copyError;
    }
    showSnackbar();
    setSnackbarMessage(intl.formatMessage(snackBarCopyMessage));
  };
  const fetchApiKeys = () => {
    setDisplayLegacyKeyWarning(false);
    HSFetch('/endpoint/org/listApiKey')
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          setApiKeys(response.data);
          const dataContainsLegacyKey = response.data.some((d: ApiKeyData) => d.is_legacy_key);

          setDisplayLegacyKeyWarning(dataContainsLegacyKey);
        } else if (response.error === 'Endpoint not enabled') {
          /**
           If user already in split with new UI loaded,
           and we turn off split, no backend action will work.
           It will look like UI is broken.
           So we need to reload page which will redirect page to old UI
           */
          window.location.reload();
        } else {
          setActionError(response.error);
        }
      })
      .catch((err) => {
        setActionError(err);
      })
      .finally(() => {
        setLoadingApiKeys(false);
      });
  };
  const generateNewApiKey = () => {
  // Get value from document because change event may not trigger if value is not changed.
    const name: string = (document.getElementById('new-name-api-key') as HTMLInputElement).value;
    setDisableGenerateKeyButton(true);
    trackHeapCustomEvent('api_key_generate_new_button_click');
    HSFetch('/endpoint/org/GenerateApiKey', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          trackHeapCustomEvent('api_key_generate_new_success');
          setIsGenerateNewAPIKeyModalOpen(false);
          fetchApiKeys();
        } else if (response.error === 'Endpoint not enabled') {
          /**
           If user already in split with new UI loaded,
           and we turn off split, no backend action will work.
           It will look like UI is broken.
           So we need to reload page which will redirect page to old UI
           */
          window.location.reload();
        } else {
          trackHeapCustomEvent('api_key_generate_new_failure', { error: response.error });
          setActionError(response.error);
        }
      })
      .catch((err) => {
        setActionError(err);
      })
      .finally(() => {
        setDisableGenerateKeyButton(false);
      });
  };
  const markApiKeyAsPrimary = (data: ApiKeyAction) => {
    trackHeapCustomEvent('api_key_mark_primary_button_click');
    setActionError('');
    HSFetch('/endpoint/org/markApiKeyAsPrimary', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guid: data.guid }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          trackHeapCustomEvent('api_key_mark_primary_success');
          setActiveActionData(activeActionDataInitialState);
          fetchApiKeys();
        } else if (response.error === 'Endpoint not enabled') {
          /**
           If user already in split with new UI loaded,
           and we turn off split, no backend action will work.
           It will look like UI is broken.
           So we need to reload page which will redirect page to old UI
           */
          window.location.reload();
        } else {
          trackHeapCustomEvent('api_key_mark_primary_failure', { error: response.error });
          setActionError(response.error);
        }
      })
      .catch((err) => {
        setActionError(err);
      });
  };
  const renameApiKey = (data: ApiKeyAction) => {
    trackHeapCustomEvent('api_key_rename_button_click');
    // Get value from document because change event may not trigger if value is not changed.
    const name: string = (document.getElementById('rename-api-key') as HTMLInputElement).value;
    HSFetch(`/endpoint/org/renameApiKey?guid=${data.guid}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          trackHeapCustomEvent('api_key_rename_success');
          setActiveActionData(activeActionDataInitialState);
          showSnackbar();
          setSnackbarMessage(intl.formatMessage(messages.renameConfirmation));
          fetchApiKeys();
        } else {
          if (response.error === 'Endpoint not enabled') {
            /**
             If user already in split with new UI loaded,
             and we turn off split, no backend action will work.
             It will look like UI is broken.
             So we need to reload page which will redirect page to old UI
             */
            window.location.reload();
          }
          trackHeapCustomEvent('api_key_rename_failure', { error: response.error });
          setActionError(response.error);
        }
      })
      .catch((err) => {
        setActionError(err);
      });
  };
  const deleteApiKey = (data: ApiKeyAction) => {
    trackHeapCustomEvent('api_key_delete_button_click');
    setActionError('');
    HSFetch('/endpoint/org/deleteApiKey', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guid: data.guid }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          trackHeapCustomEvent('api_key_delete_success');
          setActiveActionData(activeActionDataInitialState);
          showSnackbar();
          setSnackbarMessage(
            intl.formatMessage(messages.deleteConfirmation, {
              itemName: data.name,
            }),
          );
          fetchApiKeys();
        } else if (response.error === 'Endpoint not enabled') {
          // Edge case when page loaded is new noe but split got off in middle of some action
          window.location.reload();
        } else {
          trackHeapCustomEvent('api_key_delete_failure', { error: response.error });
          setActionError(response.error);
        }
      })
      .catch((err) => {
        setActionError(err);
      });
  };
  const handleSelection = (value: ApiKeyAction) => {
    setActionError('');
    setDisableRenameKeyButton(true);
    setActiveActionData({
      name: value.name,
      guid: value.guid,
      isPrimaryKey: value.isPrimaryKey,
      isLegacyKey: value.isLegacyKey,
      action: value.action,
    });
  };

  useEffect(() => {
    setLoadingApiKeys(true);
    fetchApiKeys();
    // We only want this effect to run at component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingApiKeys) {
    return (
      <div>
        <Spinner aria-valuetext={intl.formatMessage(messages.loadingSpinnerAriaValue)} size="small" />
        <FormattedMessage
          id=""
          description="Text shown while loading API key list"
          defaultMessage="Loading..." />
      </div>
    );
  }

  return (
    <div className='m-settings--section is-api no-margin'>
      {displayLegacyKeyWarning && (
        <>
          <LegacyKeyWarningBanner />
          <Spacer top={SpacerSize.MD} />
        </>
      )}
      <div className='m-settings--section--header section-header'>
        <Text size='large' isBold>
          <FormattedMessage
            id=''
            description='title, settings page, title above a section that displays the users API key.'
            defaultMessage='API key' />
        </Text>
        <DigButton
          data-qa-ref="api-key-generate-new-key-action-open-modal-button"
          variant='outline'
          className='api-section-right'
          onClick={openGenerateNewApiKeyModal}
        >
          <FormattedMessage
            id=''
            description='button text on settings page to generate new API key'
            defaultMessage='Generate key' />
        </DigButton>
      </div>
      <Table currentPage={1} allowSelect={false}>
        <Table.Head>
          <Table.Row>
            <Table.Heading>
              <FormattedMessage
                id=''
                description='The heading for a column which shows the names of API keys.'
                defaultMessage='Name' />
            </Table.Heading>
            <Table.Heading>
              <FormattedMessage
                id=''
                description='The heading for a column which shows the value of API keys.'
                defaultMessage='Value' />
            </Table.Heading>
            <Table.Heading>
              <FormattedMessage
                id=''
                description='The heading for a column which shows the created date of API keys.'
                defaultMessage='Created (UTC)' />
            </Table.Heading>
            <Table.Heading>
              <FormattedMessage
                id=''
                description='The heading for a column which shows the last used date of API keys.'
                defaultMessage='Last used (UTC)' />
            </Table.Heading>
            <Table.Heading>
              <FormattedMessage
                id=''
                description='The heading for a column which shows if API key is Primary key or not.'
                defaultMessage='Primary key' />
              <Tooltip title={intl.formatMessage(messages.primaryKeyInfoTooltip)}>
                <span><UIIcon src={InfoSmallLine} className={styles.apiIconButton} /></span>
              </Tooltip>
            </Table.Heading>
            <Table.Heading />
          </Table.Row>
        </Table.Head>
        <Table.Body data-qa-ref="api-key-generate-new-key-action-open-modal-button">
          {apiKeys && apiKeys.map((item: ApiKeyData) => (
            <Table.Row key={item.api_key}>
              <Table.Data data-qa-ref="api-key-name-data">
                <Text className={styles.apiWhitespace}>
                  {item.name}
                  {item.is_legacy_key && (
                    <Tooltip title={intl.formatMessage(messages.legacyKeyUpdateTooltip)}>
                      <span>
                        <TextTag kind={TextTagType.Danger} className={styles.apiIconButton} />
                      </span>
                    </Tooltip>
                  )}
                </Text>
              </Table.Data>
              <Table.Data data-qa-ref="api-key-partial-value-data">
                {/* Shows only the first four and last four characters of the API key. */}
                {/* Ex: "D1F3...CE40" */}
                <Text monospace>
                  {item.api_key.substring(0, 4)}...{item.api_key.slice(-4)}
                  <Tooltip title={intl.formatMessage(messages.copyApiKeyTooltip)}>
                    <span>
                      <IconButton
                        data-qa-ref="api-key-copy-action-button"
                        type='button'
                        variant='transparent'
                      >
                        <UIIcon
                          src={CopyLine}
                          onClick={() => copyToClipboard(item.api_key)}
                          className={styles.apiCopyIconButton} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Text>
              </Table.Data>
              <Table.Data data-qa-ref="api-key-created-at-data">
                <Text>
                  <RelativeDate date={new Date(item.created_at as string)} />
                </Text>
              </Table.Data>
              <Table.Data data-qa-ref="api-key-last-used-at-data">
                <Text>
                  {
                  (item.last_used_at)
                    ? <RelativeDate date={new Date(item.last_used_at as string)} />
                    : <FormattedMessage
                        id=''
                        description='The value for a column if last used at date is null or empty'
                        defaultMessage='Never' />
                }
                </Text>
              </Table.Data>
              <Table.Data data-qa-ref="api-key-is-primary-key-data">
                <Text>
                  {item.is_primary_key && (
                    <UIIcon src={CheckmarkLine} size="small" className={styles.apiIconButton} />
                  )}
                  <FormattedMessage
                    id=''
                    description='The value for a column which shows whether the API key is the primary key or not.'
                    defaultMessage='{isPrimaryKey, select, true {Yes} other {No}}'
                    values={{
                      isPrimaryKey: item.is_primary_key,
                    }} />
                </Text>
              </Table.Data>
              <Table.Data>
                <Menu.Wrapper hasMaxHeight={480}
                  onSelection={handleSelection} shouldPropagateClickOutsideMouseEvents
                >
                  {({ getTriggerProps, getContentProps }) => (
                    <>
                      <IconButton
                        data-qa-ref="api-key-menu-icon-open-button"
                        type='button'
                        variant='transparent'
                        {...getTriggerProps()}
                      >
                        <UIIcon
                          src={MoreVerticalLine}
                          aria-label={intl.formatMessage(messages.moreOptions)} />
                      </IconButton>
                      <Menu.Content
                        {...getContentProps()}
                        positioningStrategy="fixed"
                        auto={false}
                      >
                        <Menu.Segment>
                          {apiKeys.length > 1 && !item.is_primary_key && (
                            <Menu.ActionItem key='menu-mark-primary'
                              value={{
                                guid: item.guid,
                                action: 'mark-primary',
                                name: item.name,
                                isPrimaryKey: item.is_primary_key,
                                isLegacyKey: item.is_legacy_key,
                              }}
                            >
                              <DigButton
                                data-qa-ref="api-key-menu-mark-primary-button"
                                variant='transparent'
                                hasNoUnderline={true}
                                size="small"
                                withIconLeft={<UIIcon src={KeyLine} size="small" />}
                              >
                                <Body kind={BodyType.Tiny}>
                                  <FormattedMessage
                                    id=''
                                    description='Option in a drop down allowing the user to select action to mark API key as primary key'
                                    defaultMessage='Make primary' />
                                </Body>
                              </DigButton>
                            </Menu.ActionItem>
                          )}
                          <Menu.ActionItem key='menu-rename' value={{
                            guid: item.guid,
                            action: 'rename',
                            name: item.name,
                            isPrimaryKey: item.is_primary_key,
                            isLegacyKey: item.is_legacy_key,
                          }}
                          >
                            <DigButton
                              data-qa-ref="api-key-menu-rename-button"
                              variant='transparent'
                              hasNoUnderline
                              size="small"
                              withIconLeft={<UIIcon src={CursorLine} size="small" />}
                            >
                              <Body kind={BodyType.Tiny}>
                                <FormattedMessage
                                  id=''
                                  description='Option in a drop down allowing the user to select action to rename API key'
                                  defaultMessage='Rename' />
                              </Body>
                            </DigButton>
                          </Menu.ActionItem>
                          {apiKeys.length > 1 && (
                            <Menu.ActionItem key='menu-delete'
                              value={{
                                guid: item.guid,
                                action: 'delete',
                                name: item.name,
                                isPrimaryKey: item.is_primary_key,
                                isLegacyKey: item.is_legacy_key,
                              }}
                            >
                              <DigButton
                                data-qa-ref="api-key-menu-delete-button"
                                variant='transparent'
                                hasNoUnderline
                                size="small"
                                withIconLeft={<UIIcon src={DeleteLine} size='small' />}
                              >
                                <Body kind={BodyType.Tiny}>
                                  <FormattedMessage
                                    id=''
                                    description='Option in a drop down allowing the user to select action to delete API key'
                                    defaultMessage='Delete' />
                                </Body>
                              </DigButton>
                            </Menu.ActionItem>
                          )}
                        </Menu.Segment>
                      </Menu.Content>
                    </>
                  )}
                </Menu.Wrapper>
              </Table.Data>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <HelloModal
        isOpen={isGenerateNewAPIKeyModalOpen}
        onRequestClose={abortGenerateNewApiKeyModal}
        contentLabel={intl.formatMessage(messages.generateModalTitle)}
        buttons={[
          <Button
            data-qa-ref="hellomodal-generate-new-key-action-cancel-button"
            key='cancel'
            kind={ButtonType.Subtle}
            onClick={abortGenerateNewApiKeyModal}
          >
            <FormattedMessage
              id=''
              description='Cancel button on the generate API key modal'
              defaultMessage='Cancel' />
          </Button>,
          <Button
            data-qa-ref="hellomodal-generate-new-key-action-submit-button"
            key='generate-button'
            kind={ButtonType.Primary}
            disabled={disableGenerateKeyButton}
            onClick={generateNewApiKey}
          >
            <FormattedMessage
              id=''
              description='Generate key button on the generate API key modal'
              defaultMessage='Generate key' />
          </Button>,
        ]}
      >
        <TextField
          placeholder={intl.formatMessage(messages.apiKeyNamePlaceholder)}
          id='new-name-api-key'
          aria-label={intl.formatMessage(messages.apiKeyNameLabel)}
          autoComplete='off'
          onChange={changeName} />
        {displayActionError}
      </HelloModal>
      <HelloModal
        isOpen={activeActionData.action === 'mark-primary'}
        onRequestClose={abortConfirmationModal}
        contentLabel={
          intl.formatMessage(messages.markPrimaryModalTitle, {
            apiKeyName: activeActionData.name,
          })
        }
        buttons={[
          <Button
            data-qa-ref="hellomodal-mark-primary-action-cancel-button"
            key="cancel"
            kind={ButtonType.Subtle}
            onClick={abortConfirmationModal}
          >
            <FormattedMessage
              id=''
              description='Cancel button on the generate API key modal'
              defaultMessage='Cancel' />
          </Button>,
          <Button
            data-qa-ref="hellomodal-mark-primary-action-submit-button"
            key='mark-primary-button'
            kind={ButtonType.Primary}
            onClick={() => markApiKeyAsPrimary(activeActionData)}
          >
            <FormattedMessage
              id=''
              description='Make primary button on the mark API key as primary modal'
              defaultMessage='Make primary' />
          </Button>,
        ]}
      >
        <FormattedHTMLMessage
          id=''
          description='Mark primary key modal body'
          defaultMessage="Making this key primary affects the <code>event_hash</code> and <strong>Content-MD5</strong>
    values generated in callback events. If you’re currently using these hashes for verification,
    please ensure that your callback handler is ready to verify callbacks using this key first.
     <a target='_blank' href='/api/eventsAndCallbacksWalkthrough'>Learn more</a>" />
        {displayActionError}
      </HelloModal>
      <HelloModal
        isOpen={activeActionData.action === 'rename'}
        onRequestClose={abortConfirmationModal}
        contentLabel={intl.formatMessage(messages.renameModalTitle)}
        buttons={[
          <Button
            data-qa-ref="hellomodal-rename-action-cancel-button"
            key='cancel'
            kind={ButtonType.Subtle}
            onClick={abortConfirmationModal}
          >
            <FormattedMessage
              id=''
              description='Cancel button on the generate API key modal'
              defaultMessage='Cancel' />
          </Button>,
          <Button
            data-qa-ref="hellomodal-rename-action-submit-button"
            key="rename-button"
            kind={ButtonType.Primary}
            disabled={disableRenameKeyButton}
            onClick={() => renameApiKey(activeActionData)}
          >
            <FormattedMessage
              id=''
              description='Done button on the rename API key modal'
              defaultMessage='Done' />
          </Button>,
        ]}
      >
        <TextField
          placeholder={intl.formatMessage(messages.apiKeyNamePlaceholder)}
          id='rename-api-key'
          defaultValue={activeActionData.name}
          aria-label={intl.formatMessage(messages.apiKeyNameLabel)}
          autoComplete='off'
          onChange={changeName} />
        {displayActionError}
      </HelloModal>
      <HelloModal
        isOpen={activeActionData.action === 'delete' && !activeActionData.isPrimaryKey}
        onRequestClose={abortConfirmationModal}
        contentLabel={intl.formatMessage(messages.deleteModalTitle)}
        buttons={[
          <Button
            data-qa-ref="hellomodal-delete-action-cancel-button"
            key="cancel"
            kind={ButtonType.Subtle}
            onClick={abortConfirmationModal}
          >
            <FormattedMessage
              id=''
              description='Cancel button on the generate API key modal'
              defaultMessage='Cancel' />
          </Button>,
          <Button
            data-qa-ref="hellomodal-delete-action-submit-button"
            key="delete-button"
            kind={ButtonType.Primary}
            onClick={() => deleteApiKey(activeActionData)}
          >
            <FormattedMessage
              id=''
              description='Delete button on the delete API key modal'
              defaultMessage="Delete" />
          </Button>,
        ]}
      >
        <FormattedMessage
          id=''
          description='The body text for a modal which allows the user to delete their API key.'
          defaultMessage='Any applications using this key will no longer be able to access the HelloSign API. You can’t undo this action.' />
        {displayActionError}
      </HelloModal>
      <HelloModal
        isOpen={activeActionData.action === 'delete' && activeActionData.isPrimaryKey}
        onRequestClose={abortConfirmationModal}
        contentLabel={intl.formatMessage(messages.deleteWarningModalTitle)}
        buttons={[
          <Button
            data-qa-ref="hellomodal-primary-key-delete-action-cancel-button"
            key='cancel'
            kind={ButtonType.Primary}
            onClick={abortConfirmationModal}
          >
            <FormattedMessage
              id=""
              description='Dismiss button on the delete warning API key modal'
              defaultMessage='Dismiss' />
          </Button>,
        ]}
      >
        <FormattedMessage
          id=''
          description='Description text on not allowed delete action on API key'
          defaultMessage='In order to delete this key, first make any other key the Primary key.' />
      </HelloModal>
      <div>
        <Snackbar
          open={openSnackBar}
          onRequestClose={() => setOpenSnackBar(false)}
          className={styles.digSnackbarCustom}
        >
          <UIIcon src={CheckmarkLine} />
          <Snackbar.Message>
            {snackbarMessage}
          </Snackbar.Message>
          <Snackbar.Actions>
            <DigButton variant='transparent' inverse onClick={closeSnackbar} data-qa-ref="snackbar-close">
              <FormattedMessage
                id=''
                description='Close button on the snackbar notification API key setting page'
                defaultMessage='Close' />
            </DigButton>
          </Snackbar.Actions>
        </Snackbar>
      </div>
    </div>
  );
}

export default APIKeySection;
